import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { CaretDown, Plus, PencilSimple, Handbag, Heart, Leaf, Sparkle, Star } from 'phosphor-react';
import { Park } from '@phosphor-icons/react';
import { Card } from '../../components/shared/Card';
import { ReactionButtons } from '../../components/shared/ReactionButtons';
import BottomNavigation from '../../navigation/BottomNavigation';
import LocalStorage from '../../services/storage/LocalStorage';
import { createIntention } from '../../models/Intention';
import { extractDominantColor, rgbToRgba, RGB } from '../../utils/imageColorExtractor';

/**
 * Set Intent Screen
 * Displays intentions in a swipeable carousel with add/edit/delete functionality
 */
export const SetIntentScreen: React.FC = () => {
  const [intentions, setIntentions] = useState<Array<{ id: string; text: string; category?: string }>>([]);
  const [topCardIndex, setTopCardIndex] = useState(0);
  const [spotlightIntentionId, setSpotlightIntentionId] = useState<string | null>(null);
  const [editingIntentionId, setEditingIntentionId] = useState<string | null>(null);
  const [newIntentionText, setNewIntentionText] = useState('');
  const [isAddingIntention, setIsAddingIntention] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [intentionToDelete, setIntentionToDelete] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelAction, setCancelAction] = useState<(() => void) | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [intentionColors, setIntentionColors] = useState<Record<string, RGB>>({});

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate opacity based on scroll position (fade out over 100px of scroll)
  const gradientOpacity = Math.max(0, 1 - scrollY / 100);

  // Available spacebg images for intention cards from intentcards folder
  const spacebgImages = [
    '/images/intentions/intentcards/blackhole.png',
    '/images/intentions/intentcards/dust cloud.png',
    '/images/intentions/intentcards/exoplanet.png',
    '/images/intentions/intentcards/galaxy edge.png',
    '/images/intentions/intentcards/marketingaelf_A_hyper_realistic_depiction_of_a_comet_with_lig_e698c1b7-529f-47a6-bbbb-8610e1b5d4e3_0.png',
    '/images/intentions/intentcards/marketingaelf_blue_and_purple_galaxy_--ar_21_--profile_19vl5j_ac6ec024-17f0-4506-a3b0-9cb354b74058_3.png',
    '/images/intentions/intentcards/marketingaelf_green_magical_comet_--ar_21_--profile_19vl5jd_-_5942878b-3e9b-45d3-b9db-f5b87725bbdd_0.png',
    '/images/intentions/intentcards/marketingaelf_green_magical_comet_--ar_21_--profile_19vl5jd_-_5942878b-3e9b-45d3-b9db-f5b87725bbdd_2.png',
    '/images/intentions/intentcards/marketingaelf_nebula_--ar_21_--profile_19vl5jd_--v_7_87ffcd70-8b5d-43c5-b6e6-a68fa655f680_0.png',
    '/images/intentions/intentcards/marketingaelf_nebula_--ar_21_--profile_19vl5jd_--v_7_87ffcd70-8b5d-43c5-b6e6-a68fa655f680_2.png',
    '/images/intentions/intentcards/marketingaelf_purple_galaxy_--ar_21_--profile_19vl5jd_--v_7_c732112c-b9ea-41a7-b7cd-833fe08fd3d0_0.png',
    '/images/intentions/intentcards/marketingaelf_purple_galaxy_--ar_21_--profile_19vl5jd_--v_7_c732112c-b9ea-41a7-b7cd-833fe08fd3d0_1.png',
    '/images/intentions/intentcards/marketingaelf_sparkling_comet_green_teal_--ar_21_--profile_19_4c23af40-a195-4e2a-a946-feb4b984e9df_0.png',
    '/images/intentions/intentcards/marketingaelf_sparkling_comet_green_teal_--ar_21_--profile_19_4c23af40-a195-4e2a-a946-feb4b984e9df_1.png',
    '/images/intentions/intentcards/marketingaelf_sparkling_comet_green_teal_--ar_21_--profile_19_4c23af40-a195-4e2a-a946-feb4b984e9df_2.png',
    '/images/intentions/intentcards/space_2.png',
    '/images/intentions/intentcards/spiral galaxy.png'
  ];

  // Shuffle array using Fisher-Yates algorithm
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get or assign image for an intention (ensures no duplicates until all are used)
  const getIntentionImage = (intentionId: string): string => {
    const imageMappingKey = 'intention_image_mapping';
    const shuffledPoolKey = 'intention_image_shuffled_pool';
    const poolIndexKey = 'intention_image_pool_index';
    
    let imageMapping = LocalStorage.getItem<Record<string, string>>(imageMappingKey) || {};
    
    // Check if existing mapping uses old paths and migrate/clear if needed
    const existingImage = imageMapping[intentionId];
    if (existingImage) {
      // If it's an old path (not in new spacebgImages array), clear it and reassign
      if (!spacebgImages.includes(existingImage)) {
        delete imageMapping[intentionId];
        LocalStorage.setItem(imageMappingKey, imageMapping);
      } else {
        // Valid new path, return it
        return existingImage;
      }
    }
    
    // Get or create shuffled pool
    let shuffledPool = LocalStorage.getItem<string[]>(shuffledPoolKey);
    let poolIndex = LocalStorage.getItem<number>(poolIndexKey) || 0;
    
    // Validate shuffled pool - if it contains old paths, reset it
    if (shuffledPool && shuffledPool.length > 0) {
      const hasOldPaths = shuffledPool.some(path => !spacebgImages.includes(path));
      if (hasOldPaths) {
        shuffledPool = null; // Force reset
        poolIndex = 0;
      }
    }
    
    // If no pool exists or we've used all images, create a new shuffled pool
    if (!shuffledPool || poolIndex >= shuffledPool.length) {
      shuffledPool = shuffleArray(spacebgImages);
      poolIndex = 0;
      LocalStorage.setItem(shuffledPoolKey, shuffledPool);
      LocalStorage.setItem(poolIndexKey, poolIndex);
    }
    
    // Assign the next image from the shuffled pool
    const selectedImage = shuffledPool[poolIndex];
    poolIndex++;
    
    // Save the mapping and update pool index
    imageMapping[intentionId] = selectedImage;
    LocalStorage.setItem(imageMappingKey, imageMapping);
    LocalStorage.setItem(poolIndexKey, poolIndex);
    
    return selectedImage;
  };

  // Clean up image mappings for deleted intentions
  const cleanupImageMappings = (currentIntentionIds: string[]) => {
    const imageMappingKey = 'intention_image_mapping';
    const imageMapping = LocalStorage.getItem<Record<string, string>>(imageMappingKey) || {};
    const currentIds = new Set(currentIntentionIds);
    
    // Remove mappings for intentions that no longer exist
    const cleanedMapping: Record<string, string> = {};
    Object.keys(imageMapping).forEach(id => {
      if (currentIds.has(id)) {
        cleanedMapping[id] = imageMapping[id];
      }
    });
    
    if (Object.keys(cleanedMapping).length !== Object.keys(imageMapping).length) {
      LocalStorage.setItem(imageMappingKey, cleanedMapping);
    }
  };

  // Generate personalized superpower message based on intentions
  const generateSuperpowerMessage = (): string => {
    if (intentions.length === 0) {
      return 'Your superpower: Turning abstract ideas into enduring systems.';
    }

    // Calculate category distribution
    const categoryCounts = ['Wellness', 'Career', 'Love', 'Growth'].map(category => ({
      category,
      count: intentions.filter(i => (i.category || categorizeIntention(i.text)) === category).length
    }));

    // Find primary focus category
    const primaryFocus = categoryCounts.reduce((max, current) => 
      current.count > max.count ? current : max
    , categoryCounts[0]);

    // Get all unique categories with intentions
    const activeCategories = categoryCounts.filter(c => c.count > 0).map(c => c.category);
    
    // Generate message based on focus and patterns
    const messages: Record<string, string[]> = {
      'Wellness': [
        `Your superpower: Focusing on Wellness, transforming self-care into sustainable systems.`,
        `Your superpower: Building Wellness resilience through mindful daily practices.`,
        `Your superpower: Creating Wellness harmony between body, mind, and spirit.`,
        `Your superpower: Turning Wellness intentions into lasting healthy habits.`,
      ],
      'Career': [
        `Your superpower: Focusing on Career, converting professional goals into actionable achievements.`,
        `Your superpower: Building Career success through strategic planning and execution.`,
        `Your superpower: Transforming Career aspirations into tangible results.`,
        `Your superpower: Creating Career pathways from ambition to accomplishment.`,
      ],
      'Love': [
        `Your superpower: Focusing on Love, nurturing deep connections through intentional presence.`,
        `Your superpower: Building Love through meaningful relationships with authentic care.`,
        `Your superpower: Transforming Love intentions into lasting bonds.`,
        `Your superpower: Creating Love spaces for connection and understanding.`,
      ],
      'Growth': [
        `Your superpower: Focusing on Growth, turning abstract ideas into enduring systems.`,
        `Your superpower: Transforming Growth through learning into wisdom with consistent practice.`,
        `Your superpower: Building Growth through personal evolution and intentional development.`,
        `Your superpower: Creating Growth transformation through dedicated self-development.`,
      ],
    };

    // If multiple categories are balanced, use a combined message
    if (activeCategories.length > 1 && primaryFocus.count === categoryCounts.find(c => c.category !== primaryFocus.category && c.count > 0)?.count) {
      const categoryNames = activeCategories.join(' and ');
      return `Your superpower: Balancing ${categoryNames} to create holistic growth.`;
    }

    // Use primary focus category messages (only if there are intentions in that category)
    if (primaryFocus.count > 0) {
      const categoryMessages = messages[primaryFocus.category] || messages['Growth'];
      const messageIndex = primaryFocus.count % categoryMessages.length;
      return categoryMessages[messageIndex];
    }

    // Fallback
    return 'Your superpower: Turning abstract ideas into enduring systems.';
  };

  // Categorize intentions based on keywords
  const categorizeIntention = (text: string): string => {
    const lowerText = text.toLowerCase();
    
    // Wellness keywords
    if (lowerText.match(/\b(health|wellness|fitness|exercise|workout|meditation|mindfulness|yoga|diet|nutrition|sleep|mental health|physical|wellbeing|self-care|therapy|healing|recovery|stress|anxiety|depression|calm|peace|balance|energy|vitality|strength|flexibility|endurance)\b/)) {
      return 'Wellness';
    }
    // Career keywords
    if (lowerText.match(/\b(career|work|job|professional|business|success|promotion|salary|income|money|rich|wealth|financial|entrepreneur|leadership|skills|advancement|office|colleague|client|customer|project|deadline|raise|bonus|invest|save|earn|profit|revenue|startup|company|corporation|manager|director|executive|position|role|title)\b/)) {
      return 'Career';
    }
    // Love keywords
    if (lowerText.match(/\b(love|relationship|partner|dating|marriage|family|friendship|friend|connection|romance|intimacy|heart|soulmate|spouse|husband|wife|boyfriend|girlfriend|parent|child|sibling|brother|sister|mother|father|dating|single|commitment|trust|communication|bond|together|wedding|divorce|breakup|reconciliation)\b/)) {
      return 'Love';
    }
    // Growth keywords
    if (lowerText.match(/\b(learn|education|knowledge|study|course|class|hobby|passion|creativity|art|music|travel|adventure|spiritual|wisdom|confidence|gratitude|mindset|discipline|focus|productivity|habit|routine|goal|dream|aspiration|purpose|meaning|fulfillment|happiness|joy|contentment|satisfaction|achievement|accomplishment|mastery|expertise|talent|skill|ability|capability)\b/)) {
      return 'Growth';
    }
    
    return 'Growth';
  };

  // Migrate old image paths to new paths on mount
  useEffect(() => {
    const imageMappingKey = 'intention_image_mapping';
    const shuffledPoolKey = 'intention_image_shuffled_pool';
    const poolIndexKey = 'intention_image_pool_index';
    
    let imageMapping = LocalStorage.getItem<Record<string, string>>(imageMappingKey) || {};
    let shuffledPool = LocalStorage.getItem<string[]>(shuffledPoolKey);
    
    // Check if any mappings use old paths
    const needsMigration = Object.values(imageMapping).some(path => !spacebgImages.includes(path));
    const poolNeedsReset = shuffledPool && shuffledPool.some(path => !spacebgImages.includes(path));
    
    if (needsMigration || poolNeedsReset) {
      // Clear all old mappings
      imageMapping = {};
      shuffledPool = null;
      LocalStorage.setItem(imageMappingKey, imageMapping);
      LocalStorage.removeItem(shuffledPoolKey);
      LocalStorage.removeItem(poolIndexKey);
    }
  }, []);

  // Extract colors from background images
  useEffect(() => {
    if (intentions.length === 0) return;
    
    const extractColorsForIntentions = async () => {
      const colors: Record<string, RGB> = {};
      
      for (const intention of intentions) {
        const bgImage = getIntentionImage(intention.id);
        try {
          const color = await extractDominantColor(bgImage);
          colors[intention.id] = color;
        } catch (error) {
          console.error(`Failed to extract color for intention ${intention.id}:`, error);
          // Fallback to orange
          colors[intention.id] = { r: 254, g: 87, b: 6 };
        }
      }
      
      setIntentionColors(prevColors => ({ ...prevColors, ...colors }));
    };
    
    extractColorsForIntentions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentions]);

  // Load intentions on mount
  useEffect(() => {
    const stored = LocalStorage.getItem<Array<{ id: string; text: string; category?: string }>>(
      LocalStorage.getKeys().INTENTIONS
    ) || [];
    
    // Add categories to intentions if they don't have them
    const categorized = stored.map(intention => ({
      ...intention,
      category: intention.category || categorizeIntention(intention.text)
    }));
    setIntentions(categorized);
    
    // Initialize top card to first intention
    if (categorized.length > 0) {
      setTopCardIndex(0);
    }
    
    // Load spotlight intention ID
    const spotlightId = LocalStorage.getItem<string>(LocalStorage.getKeys().SPOTLIGHT_INTENTION);
    if (spotlightId && categorized.some(i => i.id === spotlightId)) {
      setSpotlightIntentionId(spotlightId);
    } else if (categorized.length > 0) {
      // Default to first intention if no spotlight is set
      const defaultSpotlightId = categorized[0].id;
      setSpotlightIntentionId(defaultSpotlightId);
      LocalStorage.setItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION, defaultSpotlightId);
    }
    
    // Auto-favorite if there's only one intention
    if (categorized.length === 1) {
      const singleIntentionId = categorized[0].id;
      setSpotlightIntentionId(singleIntentionId);
      LocalStorage.setItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION, singleIntentionId);
    }
  }, []);

  const handleSetSpotlight = (intentionId: string) => {
    // Toggle: if already spotlight, unset it; otherwise set it
    if (spotlightIntentionId === intentionId) {
      setSpotlightIntentionId(null);
      LocalStorage.removeItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION);
    } else {
      setSpotlightIntentionId(intentionId);
      LocalStorage.setItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION, intentionId);
    }
  };

  const handleSaveIntention = (id: string, newText: string) => {
    const updated = intentions.map(intention => 
      intention.id === id 
        ? { ...intention, text: newText, category: categorizeIntention(newText) }
        : intention
    );
    setIntentions(updated);
    LocalStorage.setItem(LocalStorage.getKeys().INTENTIONS, updated);
    setEditingIntentionId(null);
    setNewIntentionText('');
  };

  const handleAddIntention = () => {
    if (newIntentionText.trim()) {
      const category = categorizeIntention(newIntentionText.trim());
      const newIntention = {
        id: crypto.randomUUID(),
        text: newIntentionText.trim(),
        category: category
      };
      const updated = [newIntention, ...intentions];
      setIntentions(updated);
      LocalStorage.setItem(LocalStorage.getKeys().INTENTIONS, updated);
      
      // Assign image to new intention
      getIntentionImage(newIntention.id);
      
      setNewIntentionText('');
      setIsAddingIntention(false);
      setTopCardIndex(0); // Show the newly added card at the top

      // Auto-favorite if this becomes the only intention
      if (updated.length === 1) {
        setSpotlightIntentionId(newIntention.id);
        LocalStorage.setItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION, newIntention.id);
      }
    }
  };

  const handleDeleteIntention = (id: string) => {
    // Prevent deletion if this is the only intention
    if (intentions.length === 1) {
      return;
    }
    
    const updated = intentions.filter(intention => intention.id !== id);
    setIntentions(updated);
    LocalStorage.setItem(LocalStorage.getKeys().INTENTIONS, updated);
    
    // Clean up image mappings
    cleanupImageMappings(updated.map(i => i.id));

    // If deleted intention was the spotlight, set first remaining as spotlight
    if (spotlightIntentionId === id) {
      if (updated.length > 0) {
        const newSpotlightId = updated[0].id;
        setSpotlightIntentionId(newSpotlightId);
        LocalStorage.setItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION, newSpotlightId);
      } else {
        setSpotlightIntentionId(null);
        LocalStorage.removeItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION);
      }
    }

    // Auto-favorite if only one intention remains
    if (updated.length === 1) {
      const singleIntentionId = updated[0].id;
      setSpotlightIntentionId(singleIntentionId);
      LocalStorage.setItem(LocalStorage.getKeys().SPOTLIGHT_INTENTION, singleIntentionId);
    }
    
    // Reset top card index if needed
    if (updated.length === 0) {
      setTopCardIndex(0);
      setIsAddingIntention(true);
      setNewIntentionText('');
    } else if (topCardIndex >= updated.length) {
      setTopCardIndex(Math.max(0, updated.length - 1));
    }
    
    setShowDeleteConfirm(false);
    setIntentionToDelete(null);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Gradient header that fades on scroll */}
      <div 
        className="fixed top-0 left-0 right-0 h-[169px] z-0 pointer-events-none transition-opacity duration-300"
        style={{
          backgroundImage: 'url(/images/header/headerimg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: gradientOpacity,
        }}
      >
        {/* Gradient overlay */}
        <div 
          className="w-full h-full"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 1) 83.8%)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 pb-0 overflow-hidden">
        <div className="relative z-20 pt-[50px] pb-[106px] h-full flex flex-col min-h-0 overflow-y-auto">
          {/* Header */}
          <div className="px-screen-padding mb-6 flex-shrink-0">
            <div className="relative" style={{ height: '109px' }}>
              {/* Profile picture - positioned at right */}
              <div className="absolute right-0 top-[6px] w-[40px] h-[40px] rounded-full bg-grey-bg3 overflow-hidden">
                <img 
                  src="/images/profile/profilepicture.png" 
                  alt="Profile picture" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Title and Calendar */}
              <div className="absolute top-[56px] left-0 right-0 flex items-center justify-between">
                <span
                  style={{
                    fontFamily: 'Didot, serif',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '20px',
                    lineHeight: '24px',
                    letterSpacing: '-1px',
                    color: '#FFFFFF',
                  }}
                >
                  Your Growth Journey
                </span>
                
                {/* Calendar button */}
                <div className="flex items-center px-3 py-1.5 rounded-lg border border-white-p40/10 bg-white-p40/5 backdrop-blur-sm transition-all hover:border-white-p40/20" style={{ gap: '4px' }}>
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16.8px',
                      color: '#FFFFFF',
                    }}
                  >
                    Calendar
                  </span>
                  <CaretDown size={14} weight="bold" className="text-white-pure" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="px-screen-padding flex flex-col flex-1 min-h-0 gap-4">
            {/* Intentions Card Stack */}
            <div className="flex-1 flex flex-col min-h-0" style={{ overflow: 'visible' }}>
              <div className="relative flex-1" style={{ minHeight: '400px', overflow: 'visible' }}>
                {intentions.length > 0 ? (
                  <div className="relative w-full h-full">
                    {/* SVG gradient definitions for star icon */}
                    <svg className="gradient-icon-defs" aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0 }}>
                      <defs>
                        <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#FE5706" />
                          <stop offset="100%" stopColor="#FFA147" />
                        </linearGradient>
                      </defs>
                    </svg>
                    
                    {/* Card stack with continuous loop */}
                    {intentions.map((intention, index) => {
                      // Calculate relative position with wrapping for continuous loop
                      let relativePosition = index - topCardIndex;
                      // Handle wrapping: if distance is more than half the array, go the other way
                      if (relativePosition > intentions.length / 2) {
                        relativePosition = relativePosition - intentions.length;
                      } else if (relativePosition < -intentions.length / 2) {
                        relativePosition = relativePosition + intentions.length;
                      }
                      
                      const stackPosition = Math.abs(relativePosition);
                      const isTopCard = stackPosition === 0;
                      
                      // Only render cards that are visible (within 4 positions)
                      if (stackPosition > 3) return null;
                      
                      // Get unique image for this intention
                      const bgImage = getIntentionImage(intention.id);
                      
                      // Check if this card is liked (spotlight)
                      const isLiked = spotlightIntentionId === intention.id;
                      
                      // Get extracted color for this intention, fallback to orange
                      const cardColor = intentionColors[intention.id] || { r: 254, g: 87, b: 6 };
                      
                      // Calculate transform for stacked effect
                      const offsetY = stackPosition * 24; // 24px offset per card
                      const scale = 1 - (stackPosition * 0.05);
                      const zIndex = intentions.length - stackPosition;
                      const opacity = stackPosition > 3 ? 0 : 1 - (stackPosition * 0.1);
                      
                      return (
                        <motion.div
                          key={intention.id}
                          className="absolute"
                          style={{
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '100%',
                            zIndex: zIndex,
                            pointerEvents: isTopCard ? 'auto' : 'none',
                          }}
                          animate={{
                            y: offsetY,
                            scale: scale,
                            opacity: opacity,
                          }}
                          transition={{
                            type: 'spring',
                            stiffness: 600,
                            damping: 45,
                          }}
                          drag={isTopCard ? 'y' : false}
                          dragConstraints={{ top: -100, bottom: 100 }}
                          dragElastic={0.2}
                          dragMomentum={false}
                          dragSnapToOrigin={false}
                          onDragEnd={(_e, info: PanInfo) => {
                            if (!isTopCard) return;
                            
                            const threshold = 60;
                            const velocityThreshold = 400;
                            let newIndex = topCardIndex;
                            
                            // Only change if threshold is met
                            if (Math.abs(info.offset.y) > threshold || Math.abs(info.velocity.y) > velocityThreshold) {
                              if (info.offset.y < -threshold || info.velocity.y < -velocityThreshold) {
                                // Swipe up - bring next card to top
                                newIndex = (topCardIndex + 1) % intentions.length;
                              } else if (info.offset.y > threshold || info.velocity.y > velocityThreshold) {
                                // Swipe down - bring previous card to top
                                newIndex = topCardIndex === 0 ? intentions.length - 1 : topCardIndex - 1;
                              }
                              
                              setTopCardIndex(newIndex);
                            }
                          }}
                        >
                          <Card
                            variant="elevated"
                            className="cursor-pointer h-full flex flex-col justify-center overflow-hidden"
                            style={{
                              minHeight: '400px',
                              height: '100%',
                              boxShadow: isLiked && isTopCard
                                ? `0 0 15px ${rgbToRgba(cardColor, 0.5)}, 0 0 30px ${rgbToRgba(cardColor, 0.3)}, 0 8px 24px rgba(0, 0, 0, 0.3)`
                                : stackPosition > 0 
                                  ? `0 ${stackPosition * 4}px ${stackPosition * 8}px rgba(0, 0, 0, ${0.3 - stackPosition * 0.1})`
                                  : '0 8px 24px rgba(0, 0, 0, 0.3)',
                              position: 'relative',
                              border: isLiked && isTopCard 
                                ? `2px solid ${rgbToRgba(cardColor, 0.8)}`
                                : undefined,
                              transition: 'border 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            }}
                            overlay={
                              <>
                                <div
                                  className="absolute inset-0"
                                  style={{
                                    backgroundImage: `url(${bgImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    borderRadius: '14px',
                                    zIndex: 1,
                                  }}
                                />
                                <div
                                  className="absolute inset-0 pointer-events-none"
                                  style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    borderRadius: '14px',
                                    zIndex: 2,
                                  }}
                                />
                              </>
                            }
                          >
                            {/* Star icon for setting spotlight */}
                            {isTopCard && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetSpotlight(intention.id);
                                }}
                                className="absolute top-6 right-6 z-20 flex items-center justify-center transition-opacity active:opacity-80"
                              >
                                <Heart
                                  size={20}
                                  weight={spotlightIntentionId === intention.id ? 'fill' : 'regular'}
                                  style={
                                    spotlightIntentionId === intention.id
                                      ? { fill: 'url(#icon-gradient)' }
                                      : { color: 'rgba(255, 255, 255, 0.4)' }
                                  }
                                />
                              </button>
                            )}
                            
                            <div className="relative z-10 flex flex-col items-center justify-center h-full" style={{ paddingTop: '24px', paddingBottom: '24px' }}>
                              <span
                                style={{
                                  fontFamily: 'Didot, serif',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  fontSize: '24px',
                                  lineHeight: '1.5',
                                  color: '#FFFFFF',
                                  textAlign: 'center',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {intention.text}
                              </span>
                              {/* Category subtitle at bottom */}
                              <div className="absolute bottom-6 left-0 right-0">
                                <span
                                  style={{
                                    fontFamily: 'Space Grotesk',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    lineHeight: '16.8px',
                                    color: '#CCCCCC',
                                    textAlign: 'center',
                                    opacity: 1,
                                    display: 'block',
                                  }}
                                >
                                  {intention.category || categorizeIntention(intention.text)}
                                </span>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      );
                    }).filter(Boolean)}
                  </div>
                ) : (
                  <Card
                    variant="elevated"
                    className="cursor-pointer h-full flex flex-col justify-center !p-0 overflow-hidden"
                    style={{
                      minHeight: '400px',
                      height: '100%',
                    }}
                    overlay={
                      <>
                        <div
                          className="absolute inset-0 rounded-2xl"
                          style={{
                            backgroundImage: 'url(/images/intentions/gradient-1.png)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            zIndex: 1,
                          }}
                        />
                        <div
                          className="absolute inset-0 rounded-2xl pointer-events-none"
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            zIndex: 2,
                          }}
                        />
                      </>
                    }
                  >
                    <div className="relative z-10 flex items-center justify-center h-full">
                      <span
                        style={{
                          fontFamily: 'Didot, serif',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '24px',
                          lineHeight: '1.5',
                          color: '#FFFFFF',
                          textAlign: 'center',
                          padding: '0 24px',
                          textTransform: 'uppercase',
                        }}
                      >
                        No intentions yet
                      </span>
                    </div>
                  </Card>
                )}
              </div>
              
              {/* Subtle stack indicator */}
              {intentions.length > 1 && (
                <div className="mt-3 flex justify-center">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#CCCCCC',
                      textAlign: 'center',
                      opacity: 0.2,
                      display: 'block',
                    }}
                  >
                    Swipe up and down
                  </span>
                </div>
              )}
            </div>
            
            {/* Add/Edit/Delete buttons */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => {
                  setNewIntentionText('');
                  setIsAddingIntention(true);
                  setEditingIntentionId(null);
                }}
                disabled={isAddingIntention || editingIntentionId !== null}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-grey-bg2 text-white-pure transition-opacity active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  fontFamily: 'Space Grotesk',
                  fontSize: '12px',
                  fontWeight: 400,
                  minHeight: '44px',
                  borderRadius: '999px',
                }}
              >
                <Plus size={18} weight="regular" className="text-white-pure" style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                <span>Add</span>
              </button>
              {intentions.length > 0 && (
                <>
                  <button
                    onClick={() => {
                      const currentIntention = intentions[topCardIndex];
                      if (currentIntention) {
                        setEditingIntentionId(currentIntention.id);
                        setNewIntentionText(currentIntention.text);
                        setIsAddingIntention(false);
                      }
                    }}
                    disabled={isAddingIntention || editingIntentionId !== null}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-grey-bg2 text-white-pure transition-opacity active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '44px',
                      borderRadius: '999px',
                    }}
                  >
                    <PencilSimple size={18} weight="regular" className="text-white-pure" style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => {
                      if (intentions.length > 0 && intentions.length > 1) {
                        const currentIntention = intentions[topCardIndex];
                        if (currentIntention) {
                          setIntentionToDelete(currentIntention.id);
                          setShowDeleteConfirm(true);
                        }
                      }
                    }}
                    disabled={isAddingIntention || editingIntentionId !== null || intentions.length === 1}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-grey-bg2 text-white-pure transition-opacity active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '44px',
                      borderRadius: '999px',
                    }}
                  >
                    <span style={{ fontSize: '18px', lineHeight: '18px', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</span>
                    <span>Delete</span>
                  </button>
                </>
              )}
            </div>
            
            {/* Add new intention input */}
            {isAddingIntention && (
              <div className="flex flex-col gap-2" style={{ marginTop: '16px' }}>
                <input
                  type="text"
                  value={newIntentionText}
                  onChange={(e) => setNewIntentionText(e.target.value)}
                  placeholder="Enter your intention..."
                  className="w-full min-h-[48px] px-4 py-3 bg-grey-bg3 border border-white-p40/20 rounded-lg text-white-pure placeholder-white-p40 font-primary text-sm focus:outline-none"
                  style={{
                    fontFamily: 'Space Grotesk',
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddIntention();
                    } else if (e.key === 'Escape') {
                      setIsAddingIntention(false);
                      setNewIntentionText('');
                    }
                  }}
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleAddIntention}
                    className="px-4 py-2 rounded-lg bg-grey-bg2 text-white-pure transition-opacity active:opacity-80"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '44px',
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      if (newIntentionText.trim()) {
                        setCancelAction(() => () => {
                          setIsAddingIntention(false);
                          setNewIntentionText('');
                        });
                        setShowCancelConfirm(true);
                      } else {
                        setIsAddingIntention(false);
                        setNewIntentionText('');
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-grey-bg2 text-white-pure transition-opacity active:opacity-80"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '44px',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Edit input for current intention */}
            {editingIntentionId && intentions.length > 0 && (
              <div className="flex flex-col gap-2" style={{ marginTop: '16px' }}>
                <input
                  type="text"
                  value={newIntentionText}
                  onChange={(e) => setNewIntentionText(e.target.value)}
                  className="w-full min-h-[48px] px-4 py-3 bg-grey-bg3 border border-white-p40/20 rounded-lg text-white-pure placeholder-white-p40 font-primary text-sm focus:outline-none"
                  style={{
                    fontFamily: 'Space Grotesk',
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (newIntentionText.trim() && editingIntentionId) {
                        handleSaveIntention(editingIntentionId, newIntentionText.trim());
                      }
                    } else if (e.key === 'Escape') {
                      setEditingIntentionId(null);
                      setNewIntentionText('');
                    }
                  }}
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      if (newIntentionText.trim() && editingIntentionId) {
                        handleSaveIntention(editingIntentionId, newIntentionText.trim());
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-grey-bg2 text-white-pure transition-opacity active:opacity-80"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '44px',
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      const originalText = intentions.find(i => i.id === editingIntentionId)?.text || '';
                      if (newIntentionText.trim() !== originalText.trim()) {
                        setCancelAction(() => () => {
                          setEditingIntentionId(null);
                          setNewIntentionText('');
                        });
                        setShowCancelConfirm(true);
                      } else {
                        setEditingIntentionId(null);
                        setNewIntentionText('');
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-grey-bg2 text-white-pure transition-opacity active:opacity-80"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '44px',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Growth Journey Summary Card */}
            <Card variant="default" className="flex-shrink-0 !px-3 !py-4" style={{ marginBottom: 0 }}>
              <div className="flex flex-col gap-4">
                {/* Title and 3-dots menu */}
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '16.8px',
                      letterSpacing: '0.28px',
                      color: '#E6E6E6',
                    }}
                  >
                    Your Growth Journey
                  </span>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Category Progress Bars */}
                <div className="flex items-center gap-4 rounded-lg" style={{ paddingTop: '20px', paddingBottom: '20px', paddingLeft: '12px', paddingRight: '12px', backgroundColor: '#000000' }}>
                  {(() => {
                    // Calculate category counts
                    const categoryCounts = ['Wellness', 'Career', 'Love', 'Growth'].map(category => ({
                      category,
                      count: intentions.filter(i => (i.category || categorizeIntention(i.text)) === category).length
                    }));
                    
                    // Determine primary focus category (category with most intentions)
                    // If there's a tie, prefer the first one in the order
                    const primaryFocusCategory = categoryCounts.reduce((max, current) => 
                      current.count > max.count ? current : max
                    , categoryCounts[0]).category;
                    
                    return ['Wellness', 'Career', 'Love', 'Growth'].map((category) => {
                      const getCategoryIcon = (isPrimary: boolean) => {
                        const iconColor = isPrimary ? '#FE5706' : '#FFFFFF';
                        switch (category) {
                          case 'Wellness':
                            return <Park size={20} weight="regular" style={{ color: iconColor }} />;
                          case 'Career':
                            return <Handbag size={20} weight="regular" style={{ color: iconColor }} />;
                          case 'Love':
                            return <Heart size={20} weight="regular" style={{ color: iconColor }} />;
                          case 'Growth':
                            return <Leaf size={20} weight="regular" style={{ color: iconColor }} />;
                          default:
                            return null;
                        }
                      };
                      
                      const categoryCount = intentions.filter(i => (i.category || categorizeIntention(i.text)) === category).length;
                      const totalCount = intentions.length || 1;
                      const progress = Math.min(5, Math.ceil((categoryCount / totalCount) * 5)); // Max 5 bars
                      const isPrimaryFocus = category === primaryFocusCategory && categoryCount > 0;
                      
                      return (
                        <div
                          key={category}
                          className="flex flex-col items-center gap-2 flex-1"
                        >
                          <div className="flex flex-col gap-1">
                            {[4, 3, 2, 1, 0].map((barIdx) => (
                              <div
                                key={barIdx}
                                style={{
                                  width: '7px',
                                  height: '20px',
                                  borderRadius: '23px',
                                  backgroundColor: barIdx < progress ? '#FE5706' : '#272727',
                                  boxShadow: isPrimaryFocus && barIdx < progress
                                    ? `0 0 12px rgba(254, 87, 6, 0.7), 0 0 20px rgba(254, 87, 6, 0.4)`
                                    : 'none',
                                  animation: isPrimaryFocus && barIdx < progress
                                    ? 'glowPulseBar 3s ease-in-out infinite'
                                    : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                }}
                              />
                            ))}
                          </div>
                          <div
                            style={{
                              filter: isPrimaryFocus ? `drop-shadow(0 0 12px rgba(254, 87, 6, 0.8))` : 'none',
                              animation: isPrimaryFocus ? 'glowPulse 3s ease-in-out infinite' : 'none',
                              transition: 'filter 0.3s ease',
                            }}
                          >
                            {getCategoryIcon(isPrimaryFocus)}
                          </div>
                          <span
                            style={{
                              fontFamily: 'Space Grotesk',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '12px',
                              color: isPrimaryFocus ? '#FE5706' : '#CCCCCC',
                              textAlign: 'center',
                              textShadow: isPrimaryFocus 
                                ? `0 0 15px rgba(254, 87, 6, 0.7), 0 0 25px rgba(254, 87, 6, 0.4)`
                                : 'none',
                              animation: isPrimaryFocus ? 'glowPulseText 3s ease-in-out infinite' : 'none',
                              transition: 'text-shadow 0.3s ease',
                            }}
                          >
                            {category}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
                
                {/* Superpower Text */}
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                  <Star size={16} weight="regular" className="text-orange-primary" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '16.8px',
                      color: '#FFFFFF',
                    }}
                  >
                    {generateSuperpowerMessage()}
                  </span>
                </div>
                
                {/* Reaction buttons */}
                <ReactionButtons />
              </div>
            </Card>
            
            {/* Pursuit and Let Go Card */}
            <Card 
              variant="default" 
              className="flex-shrink-0"
              style={{ padding: '50px' }}
              overlay={
                <>
                  <div
                    className="absolute inset-0 rounded-2xl"
                    style={{
                      backgroundImage: 'url(/images/intentions/pursuit_letgo.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      zIndex: 1,
                    }}
                  />
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      zIndex: 2,
                    }}
                  />
                </>
              }
            >
              <div className="flex flex-col gap-8 relative z-10">
                {/* Two column layout for Pursuit and Let go */}
                <div className="flex items-start justify-between gap-4">
                  {/* Pursuit Column */}
                  <div className="flex flex-col gap-3 flex-1">
                    <span
                      style={{
                        fontFamily: 'Didot, serif',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '24px',
                        letterSpacing: '0.4px',
                        color: '#E6E6E6',
                      }}
                    >
                      Pursuit
                    </span>
                    <div className="flex flex-col gap-2">
                      {[
                        'Take a walk',
                        'Meditate',
                        'Tidy up',
                        'Text a friend',
                        'Drink more water'
                      ].map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <span
                            style={{
                              fontFamily: 'Space Grotesk',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '24px',
                              color: '#E6E6E6',
                              minWidth: '20px',
                            }}
                          >
                            {index + 1}.
                          </span>
                          <span
                            style={{
                              fontFamily: 'Space Grotesk',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '24px',
                              color: '#E6E6E6',
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Let go Column */}
                  <div className="flex flex-col gap-3 flex-1">
                    <span
                      style={{
                        fontFamily: 'Didot, serif',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '24px',
                        letterSpacing: '0.4px',
                        color: '#E6E6E6',
                      }}
                    >
                      Let Go
                    </span>
                    <div className="flex flex-col gap-2">
                      {[
                        'Buy stocks',
                        'Go out aimlessly',
                        'Argue',
                        'Overshare',
                        'Start new plans'
                      ].map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <span
                            style={{
                              fontFamily: 'Space Grotesk',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '24px',
                              color: '#E6E6E6',
                              minWidth: '20px',
                            }}
                          >
                            {index + 1}.
                          </span>
                          <span
                            style={{
                              fontFamily: 'Space Grotesk',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '24px',
                              color: '#E6E6E6',
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Ask and reflect button */}
                <div className="flex justify-center relative z-10 flex-shrink-0">
                  <button
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-[15px] border border-white-p90 h-9 transition-all active:bg-white-pure [&:active_span]:!text-black [&:active_span]:!opacity-100 [&:active>div>img]:brightness-0"
                    onClick={() => {
                      console.log('Button clicked');
                    }}
                  >
                    <span
                      className="transition-colors"
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '28px',
                        color: '#E6E6E6',
                      }}
                    >
                      Ask and reflect with
                    </span>
                    <div className="flex items-center gap-1.5">
                      <img 
                        src="/svg/godgpticon.svg" 
                        alt="GodGPT Icon" 
                        className="w-[13px] h-4 transition-all group-active:brightness-0"
                      />
                      <span
                        className="transition-colors"
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '28px',
                          color: '#E6E6E6',
                        }}
                      >
                        GodGPT
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => {
            setShowCancelConfirm(false);
            setCancelAction(null);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0f0f0f',
              border: '1px solid #4d4d4d',
              borderRadius: '15px',
              padding: '20px',
              width: '313px',
              minHeight: '143px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {/* Title */}
            <div>
              <span
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '22.4px',
                  letterSpacing: '0.28px',
                  color: '#FFFFFF',
                  display: 'block',
                }}
              >
                Discard Changes?
              </span>
            </div>
            
            {/* Body text */}
            <div>
              <span
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22.4px',
                  letterSpacing: '0.28px',
                  color: '#FFFFFF',
                  display: 'block',
                }}
              >
                Your changes will be lost. Are you sure you want to cancel?
              </span>
            </div>
            
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelAction(null);
                }}
                style={{
                  flex: 1,
                  height: '43px',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '10px',
                  border: 'none',
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '25.6px',
                  letterSpacing: '0.32px',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  if (cancelAction) {
                    cancelAction();
                  }
                  setShowCancelConfirm(false);
                  setCancelAction(null);
                }}
                style={{
                  flex: 1,
                  height: '43px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  border: 'none',
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '25.6px',
                  letterSpacing: '0.32px',
                  color: '#010100',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Discard
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => {
            setShowDeleteConfirm(false);
            setIntentionToDelete(null);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0f0f0f',
              border: '1px solid #4d4d4d',
              borderRadius: '15px',
              padding: '20px',
              width: '313px',
              minHeight: '143px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {/* Title */}
            <div>
              <span
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '22.4px',
                  letterSpacing: '0.28px',
                  color: '#FFFFFF',
                  display: 'block',
                }}
              >
                {intentions.length === 1 ? 'Delete Your Last Intention?' : 'Delete Intention?'}
              </span>
            </div>
            
            {/* Body text */}
            <div>
              <span
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22.4px',
                  letterSpacing: '0.28px',
                  color: '#FFFFFF',
                  display: 'block',
                }}
              >
                {intentions.length === 1 
                  ? 'This is your last intention. Deleting it will clear your intentions. You can add a new one anytime.'
                  : 'This action cannot be undone. Are you sure you want to delete this intention?'}
              </span>
            </div>
            
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setIntentionToDelete(null);
                }}
                style={{
                  flex: 1,
                  height: '43px',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '10px',
                  border: 'none',
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '25.6px',
                  letterSpacing: '0.32px',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (intentionToDelete) {
                    handleDeleteIntention(intentionToDelete);
                  }
                  setShowDeleteConfirm(false);
                  setIntentionToDelete(null);
                }}
                style={{
                  flex: 1,
                  height: '43px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  border: 'none',
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '25.6px',
                  letterSpacing: '0.32px',
                  color: '#010100',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default SetIntentScreen;
